import { Card } from '@repo/shadcn';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import {
    AutoCompleteInput,
    Option,
} from '@components/ui/autocomplete/auto-complete-input';

import { useGetAllPurchasesQuery } from '@/features/slice/purchases';
import { toastError } from '@/utils';

import { useCreatePurchaseReturnContext } from './create-purchase-return.context';
import { TCreatePurchaseReturnForm } from './create-purchase-return.schema';

export const PurchaseSelector = () => {
    const { data, isLoading, isFetching } = useGetAllPurchasesQuery({});

    const { setSelectedPurchaseId, selectedPurchaseId, getPurchaseDetails } =
        useCreatePurchaseReturnContext();

    const { reset, setValue } = useFormContext<TCreatePurchaseReturnForm>();

    const handleSelectPurchase = (option: Option) => {
        if (!option.value) {
            reset();
        }
        setSelectedPurchaseId(option.value);
    };

    useEffect(() => {
        if (selectedPurchaseId) {
            getPurchaseDetails(selectedPurchaseId)
                .unwrap()
                .then((response) => {
                    reset();
                    setValue(
                        'items',
                        response.data?.items?.map((item: any) => ({
                            purchase_item_id: item.id,
                            purchase_item_name: item.product?.name || '',
                            product_variation_id:
                                item.product?.product_variation_id,
                            quantity: item.quantity,
                            return_quantity: item.quantity,
                            price: item.purchase_price,
                            is_selected: false,
                        })) || [],
                    );
                    setValue('reason', '');
                })
                .catch((error) =>
                    toastError(error, 'Failed to load purchase details'),
                );
        }
    }, [selectedPurchaseId]);

    const options =
        data?.data?.map((purchase) => {
            return {
                label: purchase.invoice_number || `Purchase #${purchase.id}`,
                value: purchase.id,
            };
        }) || [];

    const selectedValue = options.find(
        (option) => option.value === selectedPurchaseId,
    ) ?? {
        label: '',
        value: '',
    };

    return (
        <Card className={'p-4'}>
            <AutoCompleteInput
                options={options}
                emptyMessage={'No purchases found'}
                placeholder={'Select Purchase'}
                isLoading={isLoading || isFetching}
                onValueChange={handleSelectPurchase}
                value={selectedValue}
            />
        </Card>
    );
};

PurchaseSelector.displayName = 'PurchaseSelector';
